import type { GuestWishlistEntry } from '~/utils/guestWishlist'
import { parseGuestWishlist, serializeGuestWishlist } from '~/utils/guestWishlist'

/**
 * The logged-out visitor's saved items, kept in localStorage so they survive a
 * reload and a return visit, and merged into the account on sign-in.
 *
 * localStorage rather than sessionStorage: this outlives the visit by design —
 * losing a saved item silently is the failure the guest-first flow exists to
 * prevent. It is deliberately never a cookie, which would put the list into
 * every request and into the very HTML that has to stay CDN-cacheable.
 */

const STORAGE_KEY = 'guest-wishlist'

export function useGuestWishlist() {
  // Shared reactive copy. Starts empty on the server and stays empty through
  // hydration, so a cached document carries nobody's wishlist.
  const entries = useState<GuestWishlistEntry[]>(STORAGE_KEY, () => [])

  // False on the server and through hydration, true once storage has been read.
  // The wishlist page waits on it so a guest sees a spinner rather than a flash
  // of "your wishlist is empty" before their saved rows arrive.
  const loaded = useState<boolean>(`${STORAGE_KEY}:loaded`, () => false)

  /** `null` means storage could not be read at all, which is not the same as empty. */
  function readStorage(): GuestWishlistEntry[] | null {
    if (!import.meta.client)
      return null

    try {
      return parseGuestWishlist(window.localStorage.getItem(STORAGE_KEY))
    }
    catch {
      // Hardened profiles and Safari's private mode throw on the first touch.
      return null
    }
  }

  /**
   * Applies a change against what is *in storage right now* rather than against
   * this tab's copy. Shopping across several tabs is ordinary, and writing a
   * snapshot taken at mount would silently discard whatever the other tab saved
   * in the meantime.
   */
  function mutate(apply: (current: GuestWishlistEntry[]) => GuestWishlistEntry[]): void {
    const next = apply(readStorage() ?? entries.value)
    entries.value = next

    if (!import.meta.client)
      return

    try {
      window.localStorage.setItem(STORAGE_KEY, serializeGuestWishlist(next))
    }
    catch {
      // Storage unavailable — the in-memory list still serves this visit.
    }
  }

  /**
   * Pulls the stored list into memory. Called once from a client plugin after
   * the app has mounted, never during setup: reading earlier would make the
   * first client render disagree with the server-rendered (and possibly cached)
   * HTML, and every heart on the page would hydration-mismatch.
   */
  function load(): void {
    entries.value = readStorage() ?? []
    loaded.value = true
  }

  /** Re-reads after another tab wrote, so both windows show the same hearts. */
  function syncFromStorage(): void {
    const stored = readStorage()
    if (stored)
      entries.value = stored
  }

  function has(variantId: string): boolean {
    return entries.value.some(entry => entry.variantId === String(variantId))
  }

  function add(entry: GuestWishlistEntry): void {
    const variantId = String(entry.variantId)
    const productId = String(entry.productId)

    mutate(current => current.some(existing => existing.variantId === variantId)
      ? current
      : [...current, { variantId, productId, ...(entry.sizeSelected ? { sizeSelected: true } : {}) }])
  }

  function remove(variantId: string): void {
    mutate(current => current.filter(entry => entry.variantId !== String(variantId)))
  }

  /**
   * Drops entries the catalogue no longer resolves — disabled, deleted, or moved
   * out of this channel. Without it the wishlist page would keep hiding a row
   * the header badge keeps counting, and nothing in the UI could reconcile them.
   */
  function keepOnly(variantIds: Iterable<string>): void {
    const keep = new Set<string>()
    for (const id of variantIds)
      keep.add(String(id))

    if (entries.value.every(entry => keep.has(entry.variantId)))
      return

    mutate(current => current.filter(entry => keep.has(entry.variantId)))
  }

  function clear(): void {
    mutate(() => [])
  }

  return { entries, loaded, load, syncFromStorage, has, add, remove, keepOnly, clear }
}
